import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserPlus, Shield, KeyRound, Plus, Search, CheckCircle, XCircle,
  Eye, RefreshCw, Loader2, Trash2, Copy, Ticket, Calendar, Ban,
  Mail, Phone, User, FileText, Languages, Briefcase, MapPin, X,
  ExternalLink, Award, Lock, Unlock, Archive, AlertTriangle, Download,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import AgentAnalyticsBanner from './AgentAnalyticsBanner';
import AgentDiagnosticDialog from './AgentDiagnosticDialog';
import AgentInvites from '../../AgentInvites';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Robust CV opener — handles all URL flavours a merchant platform may send.
 *
 *   1. Empty / null / '-'          → toast: no CV attached
 *   2. `data:` URI (base64 PDF/IMG) → open in new tab via a blob URL
 *      (many browsers block direct navigation to data URIs from anchors)
 *   3. Relative path (`/uploads/…`) → prepend REACT_APP_BACKEND_URL
 *   4. Absolute URL (`https://…`)   → open directly
 *
 * Always returns a Promise so callers can await if they want to catch errors.
 */
async function openCV(rawUrl, { isAr = true } = {}) {
  const t = (ar, en) => (isAr ? ar : en);
  if (!rawUrl || typeof rawUrl !== 'string' || rawUrl.trim() === '' || rawUrl.trim() === '-') {
    toast.error(t('لا توجد سيرة ذاتية مرفقة', 'No CV attached'));
    return;
  }

  const url = rawUrl.trim();

  try {
    if (url.startsWith('data:')) {
      // Convert data: URI → blob URL so the browser opens it reliably.
      const [meta, base64] = url.split(',');
      if (!base64) throw new Error('malformed data URL');
      const mimeMatch = /data:([^;]+)/.exec(meta);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const byteChars = atob(base64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNums)], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!w) toast.error(t('فتح النافذة محظور — يُرجى السماح بالنوافذ المنبثقة', 'Popup blocked — please allow popups'));
      // Revoke after 60s so memory is freed (still enough time to view / download).
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) toast.error(t('فتح النافذة محظور — يُرجى السماح بالنوافذ المنبثقة', 'Popup blocked — please allow popups'));
      return;
    }

    // Relative path — prepend backend URL
    const fullUrl = `${API_URL || ''}${url.startsWith('/') ? '' : '/'}${url}`;
    const w = window.open(fullUrl, '_blank', 'noopener,noreferrer');
    if (!w) toast.error(t('فتح النافذة محظور — يُرجى السماح بالنوافذ المنبثقة', 'Popup blocked — please allow popups'));
  } catch (err) {
    toast.error(t('تعذّر فتح السيرة الذاتية', 'Could not open the CV') + ' — ' + err.message);
  }
}

/**
 * Normalise the wildly different shapes an agent_request can take from the
 * merchant-platform side (snake_case, camelCase, nested fields, etc).
 *
 * Includes top-level fields from the merchant-platform REST API:
 *   fullNameAr, fullNameEn, bgm_number, cv, languages[], experience, ...
 */
function extractAgentDetails(req) {
  const r = req || {};
  const u = r.user || {};
  const cv = r.cv_url || r.cvUrl || r.cv || r.cv_link || r.resume_url || r.resumeUrl;
  let languages = r.languages || r.spoken_languages || u.languages || [];
  if (typeof languages === 'string') {
    languages = languages.split(/[,،]/).map(s => s.trim()).filter(Boolean);
  }
  if (!Array.isArray(languages)) languages = [];
  return {
    cv_url: typeof cv === 'string' ? cv : null,
    languages,
    bio: r.bio || r.about || r.cover_letter || r.motivation || u.bio || null,
    skills: r.skills || r.expertise || u.skills || [],
    experience: r.experience || r.years_experience || r.years_of_experience || null,
    nationality: r.nationality || u.nationality || null,
    city: r.city || r.location || u.city || u.location || null,
    address: r.address || u.address || null,
    education: r.education || r.qualifications || null,
    portfolio: r.portfolio_url || r.portfolio || r.website || null,
    linkedin: r.linkedin || r.linkedin_url || null,
    note: r.note || r.notes || r.comments || null,
    // Top-level REST API extras
    full_name_ar: r.fullNameAr || r.full_name_ar || r.fullname_ar || u.fullNameAr || u.full_name_ar || null,
    full_name_en: r.fullNameEn || r.full_name_en || r.fullname_en || u.fullNameEn || u.full_name_en || null,
    bgm_number: r.bgm_number || r.bgmNumber || u.bgm_number || u.bgmNumber || null,
    email: r.email || u.email || null,
    phone: r.phone || r.mobile || u.phone || u.mobile || null,
  };
}

/** Best-effort display name: prefer AR, then EN, then user.name, then fallback. */
function displayAgentName(req) {
  const r = req || {};
  return (
    r.fullNameAr || r.full_name_ar ||
    r.fullNameEn || r.full_name_en ||
    r.user?.name || r.name || 'مستخدم'
  );
}

/**
 * Agents-related tabs grouped together:
 *  - agent-requests (agent membership requests)
 *  - agents (company agents list)
 *  - invitation-codes (invitation codes management)
 * Extracted from ManagePlatform.js.
 */
export default function AgentsSection({
  // Common
  activeTab,
  language = 'ar',
  loading,
  token,
  // Agent requests
  agentRequests,
  getFilteredAgentRequests,
  agentRequestFilter,
  setAgentRequestFilter,
  approveAgentRequest,
  processingAgent,
  setSelectedAgentRequest,
  setShowRejectAgentModal,
  // Agents
  approvedAgents,
  revokeAgent,
  blockAgent,
  unblockAgent,
  archiveAgent,
  // Invitation codes
  invitationCodes,
  setShowCreateCodeModal,
  copyCode,
  deleteInvitationCode,
}) {
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const navigate = useNavigate();
  const [viewAgent, setViewAgent] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { agent, kind, reason }
  const [diagOpen, setDiagOpen] = useState(false);

  // ─── Sub-filters (per-tab) ────────────────────────────────────────
  // Applied on top of the status filter. Kept local to this component so
  // parent's state stays lean.
  const [subEmail, setSubEmail] = useState('');
  const [subDateFrom, setSubDateFrom] = useState('');
  const [subDateTo, setSubDateTo] = useState('');

  const filteredRequestsBase = getFilteredAgentRequests ? getFilteredAgentRequests() : [];
  const filteredRequests = filteredRequestsBase.filter((r) => {
    if (subEmail) {
      const q = subEmail.trim().toLowerCase();
      const hay = [
        r.email, r.fullName, r.fullNameEn, r.name, r.request_number,
        r.phone, r.agent_id, r.bgm_number,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (subDateFrom) {
      const created = r.created_at ? new Date(r.created_at).getTime() : 0;
      const from = new Date(subDateFrom).getTime();
      if (!Number.isNaN(from) && created && created < from) return false;
    }
    if (subDateTo) {
      const created = r.created_at ? new Date(r.created_at).getTime() : 0;
      const to = new Date(subDateTo).getTime() + 86_399_999; // include full day
      if (!Number.isNaN(to) && created && created > to) return false;
    }
    return true;
  });

  // Pending count (only badge we show on the switch — user request iter-138).
  const pendingCount = (agentRequests || []).filter((r) => r.status === 'pending').length;
  return (
    <>
            {activeTab === 'agent-requests' && (
              <div>
                <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <UserPlus className="h-6 w-6 text-purple-500" />
                    {t('طلبات عضوية وكيل', 'Agent Membership Requests')}
                    <span className="text-sm font-normal text-[#808080]">
                      ({filteredRequests.length}
                      {filteredRequests.length !== filteredRequestsBase.length && (
                        <span className="text-white/40"> / {filteredRequestsBase.length}</span>
                      )})
                    </span>
                  </h2>

                  {/* Status switch — order: pending → approved → rejected → all */}
                  <div className="flex items-center gap-2 bg-[#0a0a0a] p-1 rounded-xl border border-[#2a2a4e]">
                    {[
                      {
                        value: 'pending',
                        label: t('قيد المراجعة', 'Pending'),
                        color: 'amber',
                        badge: pendingCount > 0 ? pendingCount : null,
                      },
                      { value: 'approved', label: t('موافق عليه', 'Approved'), color: 'emerald' },
                      { value: 'rejected', label: t('مرفوض', 'Rejected'), color: 'red' },
                      { value: 'all', label: t('الكل', 'All'), color: 'gray' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setAgentRequestFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1.5 ${
                          agentRequestFilter === opt.value
                            ? `bg-${opt.color}-600 text-white`
                            : 'text-[#808080] hover:text-white'
                        }`}
                        data-testid={`agent-requests-filter-${opt.value}`}
                      >
                        {opt.label}
                        {opt.badge != null && (
                          <span
                            className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                              agentRequestFilter === opt.value
                                ? 'bg-white/25 text-white'
                                : `bg-${opt.color}-500/25 text-${opt.color}-300`
                            }`}
                            data-testid="agent-requests-filter-pending-count"
                          >
                            {opt.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sub-filters — email search + date range */}
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="relative">
                    <Search className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" style={isAr ? { right: 10 } : { left: 10 }} />
                    <Input
                      value={subEmail}
                      onChange={(e) => setSubEmail(e.target.value)}
                      placeholder={t('بحث بالبريد أو الاسم أو رقم الوكيل', 'Search email / name / agent #')}
                      className={`bg-[#050505] border-white/10 text-white text-sm ${isAr ? 'pr-9' : 'pl-9'}`}
                      data-testid="agent-requests-sub-search"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/40 mb-1">{t('من تاريخ', 'From date')}</label>
                    <Input
                      type="date"
                      value={subDateFrom}
                      onChange={(e) => setSubDateFrom(e.target.value)}
                      className="bg-[#050505] border-white/10 text-white text-sm"
                      data-testid="agent-requests-sub-date-from"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/40 mb-1">{t('إلى تاريخ', 'To date')}</label>
                    <Input
                      type="date"
                      value={subDateTo}
                      onChange={(e) => setSubDateTo(e.target.value)}
                      className="bg-[#050505] border-white/10 text-white text-sm"
                      data-testid="agent-requests-sub-date-to"
                    />
                  </div>
                  {(subEmail || subDateFrom || subDateTo) && (
                    <button
                      onClick={() => { setSubEmail(''); setSubDateFrom(''); setSubDateTo(''); }}
                      className="sm:col-span-3 justify-self-start text-xs text-white/50 hover:text-white/80 inline-flex items-center gap-1"
                      data-testid="agent-requests-sub-clear"
                    >
                      <X className="h-3 w-3" />
                      {t('مسح كل الفلاتر', 'Clear all filters')}
                    </button>
                  )}
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-24 bg-[#0a0a0a] rounded-xl animate-pulse" />)}
                  </div>
                ) : filteredRequests.length === 0 ? (
                  <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                    <UserPlus className="h-16 w-16 mx-auto mb-4 opacity-30 text-purple-500" />
                    <p className="text-lg">{t('لا توجد طلبات عضوية وكيل', 'No agent membership requests')}</p>
                    <p className="text-sm mt-2 text-[#606060]">{t('ستظهر هنا طلبات الوكالة المقدمة من منصة التجار', 'Merchant platform agent applications will appear here')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRequests.map((req) => {
                      const statusConfig = {
                        pending: { label: t('قيد المراجعة', 'Pending'), bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
                        approved: { label: t('موافق عليه', 'Approved'), bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
                        rejected: { label: t('مرفوض', 'Rejected'), bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
                      };
                      const status = statusConfig[req.status] || statusConfig.pending;
                      const details = extractAgentDetails(req);
                      
                      return (
                        <div 
                          key={req.id} 
                          className="p-5 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] hover:border-purple-500/50 transition-all"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                  <User className="h-6 w-6 text-purple-500" />
                                </div>
                                <div>
                                  <h3 className="font-bold text-white" data-testid={`agent-req-name-${req.id}`}>
                                    {displayAgentName(req)}
                                  </h3>
                                  {details.full_name_en && details.full_name_ar && (
                                    <p className="text-[10px] text-[#71717A]" dir="ltr">{details.full_name_en}</p>
                                  )}
                                  <p className="text-sm text-[#808080]">{details.email || req.user?.email}</p>
                                  {(details.phone || req.user?.phone) && (
                                    <p className="text-xs text-[#606060] font-mono" dir="ltr">
                                      {details.phone || req.user.phone}
                                    </p>
                                  )}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text} border ${status.border}`}>
                                  {status.label}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div className="bg-[#0a0a0a] p-3 rounded-xl">
                                  <p className="text-[#606060] text-xs mb-1">{t('نوع الطلب', 'Request Type')}</p>
                                  <p className="text-white font-medium">
                                    {req.request_type === 'invitation' ? t('دعوة', 'Invitation') : t('تقديم مباشر', 'Direct Application')}
                                  </p>
                                </div>
                                <div className="bg-[#0a0a0a] p-3 rounded-xl">
                                  <p className="text-[#606060] text-xs mb-1">{t('تاريخ التقديم', 'Submitted On')}</p>
                                  <p className="text-white font-medium">
                                    {req.created_at ? new Date(req.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '-'}
                                  </p>
                                </div>
                                {req.invitation_code && (
                                  <div className="bg-[#0a0a0a] p-3 rounded-xl">
                                    <p className="text-[#606060] text-xs mb-1">{t('كود الدعوة', 'Invitation Code')}</p>
                                    <p className="text-amber-500 font-mono font-medium">{req.invitation_code}</p>
                                  </div>
                                )}
                                {req.agent_id && (
                                  <div className="bg-[#0a0a0a] p-3 rounded-xl">
                                    <p className="text-[#606060] text-xs mb-1">{t('رقم الوكيل', 'Agent ID')}</p>
                                    <p className="text-emerald-500 font-mono font-medium">{req.agent_id}</p>
                                  </div>
                                )}
                                {details.bgm_number && !req.agent_id && (
                                  <div className="bg-[#0a0a0a] p-3 rounded-xl" data-testid={`agent-req-bgm-${req.id}`}>
                                    <p className="text-[#606060] text-xs mb-1">{t('رقم العضوية (BGM)', 'Membership # (BGM)')}</p>
                                    <p className="text-cyan-400 font-mono font-medium">{details.bgm_number}</p>
                                  </div>
                                )}
                              </div>

                              {/* Application details — CV, languages, bio, skills */}
                              {(details.cv_url || details.languages.length > 0 || details.bio || details.skills.length > 0 || details.experience || details.nationality || details.city) && (
                                <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  {details.cv_url && (
                                    <button
                                      type="button"
                                      onClick={() => openCV(details.cv_url, { isAr })}
                                      className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-lg hover:bg-blue-500/20 transition text-left"
                                      data-testid={`agent-cv-link-${req.id}`}
                                    >
                                      <FileText className="h-4 w-4" />
                                      <span>{t('عرض السيرة الذاتية (CV)', 'View CV')}</span>
                                      <ExternalLink className="h-3 w-3 ms-auto" />
                                    </button>
                                  )}
                                  {details.languages.length > 0 && (
                                    <div className="flex items-start gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <Languages className="h-4 w-4 text-purple-400 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-[#606060]">{t('اللغات', 'Languages')}</p>
                                        <p className="text-white truncate">{details.languages.join(isAr ? '، ' : ', ')}</p>
                                      </div>
                                    </div>
                                  )}
                                  {details.experience && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <Briefcase className="h-4 w-4 text-cyan-400" />
                                      <div className="min-w-0">
                                        <p className="text-[10px] text-[#606060]">{t('الخبرة', 'Experience')}</p>
                                        <p className="text-white truncate">{details.experience}</p>
                                      </div>
                                    </div>
                                  )}
                                  {details.nationality && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <Award className="h-4 w-4 text-amber-400" />
                                      <div className="min-w-0">
                                        <p className="text-[10px] text-[#606060]">{t('الجنسية', 'Nationality')}</p>
                                        <p className="text-white truncate">{details.nationality}</p>
                                      </div>
                                    </div>
                                  )}
                                  {details.city && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <MapPin className="h-4 w-4 text-emerald-400" />
                                      <div className="min-w-0">
                                        <p className="text-[10px] text-[#606060]">{t('المدينة / الموقع', 'City / Location')}</p>
                                        <p className="text-white truncate">{details.city}</p>
                                      </div>
                                    </div>
                                  )}
                                  {details.skills.length > 0 && (
                                    <div className="md:col-span-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <p className="text-[10px] text-[#606060] mb-1.5">{t('المهارات', 'Skills')}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {(Array.isArray(details.skills) ? details.skills : [details.skills]).map((sk, i) => (
                                          <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-purple-500/20 text-purple-300">
                                            {sk}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {details.bio && (
                                    <div className="md:col-span-2 px-3 py-2 bg-white/5 rounded-lg">
                                      <p className="text-[10px] text-[#606060] mb-1">{t('نبذة / رسالة الوكيل', 'Bio / Cover Letter')}</p>
                                      <p className="text-white text-xs leading-relaxed">{details.bio}</p>
                                    </div>
                                  )}
                                  {(details.portfolio || details.linkedin) && (
                                    <div className="md:col-span-2 flex gap-2 flex-wrap">
                                      {details.portfolio && (
                                        <a href={details.portfolio} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 underline">Portfolio</a>
                                      )}
                                      {details.linkedin && (
                                        <a href={details.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 underline">LinkedIn</a>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {req.status === 'rejected' && req.reject_reason && (
                                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                                  <p className="text-xs text-red-400 mb-1">{t('سبب الرفض:', 'Rejection reason:')}</p>
                                  <p className="text-sm text-white">{req.reject_reason}</p>
                                </div>
                              )}
                            </div>

                            {/* Action buttons */}
                            {req.status === 'pending' && (
                              <div className="flex flex-col gap-2">
                                <Button
                                  onClick={() => approveAgentRequest(req)}
                                  disabled={processingAgent === req.id}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                                  data-testid={`agent-approve-${req.id}`}
                                >
                                  {processingAgent === req.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <CheckCircle className="h-4 w-4 me-2" />
                                      {t('موافقة', 'Approve')}
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => {
                                    setSelectedAgentRequest(req);
                                    setShowRejectAgentModal(true);
                                  }}
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 rounded-xl"
                                  data-testid={`agent-reject-${req.id}`}
                                >
                                  <XCircle className="h-4 w-4 me-2" />
                                  {t('رفض', 'Reject')}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Company Agents Section */}
            {activeTab === 'agents' && (
              <div>
                <AgentAnalyticsBanner token={token} />
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Shield className="h-6 w-6 text-cyan-500" />
                    {t('وكلاء الشركة', 'Company Agents')}
                    <span className="text-sm font-normal text-[#808080]">({approvedAgents.length})</span>
                  </h2>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-20 bg-[#0a0a0a] rounded-xl animate-pulse" />)}
                  </div>
                ) : approvedAgents.length === 0 ? (
                  <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                    <Shield className="h-16 w-16 mx-auto mb-4 opacity-30 text-cyan-500" />
                    <p className="text-lg">{t('لا يوجد وكلاء معتمدين', 'No approved agents')}</p>
                    <p className="text-sm mt-2 text-[#606060]">{t('ستظهر هنا قائمة الوكلاء المعتمدين بعد الموافقة على طلباتهم', 'Approved agents will appear here once their requests are approved')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {approvedAgents.map((agent) => {
                      const isBlocked = !!agent.agent_blocked;
                      return (
                        <div
                          key={agent.id}
                          className={`p-4 bg-[#1a1a2e] rounded-2xl border transition-all ${
                            isBlocked
                              ? 'border-red-500/30 hover:border-red-500/50'
                              : 'border-[#2a2a4e] hover:border-cyan-500/50'
                          }`}
                          data-testid={`agent-row-${agent.id}`}
                        >
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            {/* Left: identity + status */}
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                isBlocked ? 'bg-red-500/20' : 'bg-cyan-500/20'
                              }`}>
                                <Shield className={`h-6 w-6 ${isBlocked ? 'text-red-400' : 'text-cyan-500'}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-bold text-white truncate">{agent.name}</h3>
                                  {isBlocked && (
                                    <span
                                      className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/40 flex items-center gap-1"
                                      data-testid={`agent-blocked-badge-${agent.id}`}
                                    >
                                      <Lock className="h-2.5 w-2.5" />
                                      {t('محظور', 'Blocked')}
                                    </span>
                                  )}
                                  <span className="px-2 py-0.5 rounded-md text-[10px] font-mono bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                                    {agent.agent_id}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-xs text-[#808080] flex-wrap">
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    <span className="truncate">{agent.email}</span>
                                  </span>
                                  {agent.phone && (
                                    <span className="flex items-center gap-1" dir="ltr">
                                      <Phone className="h-3 w-3" />
                                      <span className="font-mono">{agent.phone}</span>
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {agent.agent_approved_at ? new Date(agent.agent_approved_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '-'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Right: action buttons */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {/* iter-244 — Eye button now navigates to the
                                 full-page agent details view (with tasks +
                                 registrations). The legacy modal
                                 (`setViewAgent`) is preserved for other
                                 sections. */}
                              <button
                                onClick={() => navigate(`/admin/agents/${agent.id || agent.agent_id}`)}
                                className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-colors"
                                title={t('عرض التفاصيل الكاملة', 'View full details')}
                                data-testid={`agent-view-${agent.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              {/* Block / Unblock (toggle) */}
                              {isBlocked ? (
                                <button
                                  onClick={() => setConfirmAction({ agent, kind: 'unblock' })}
                                  className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                                  title={t('فك الحظر', 'Unblock')}
                                  data-testid={`agent-unblock-${agent.id}`}
                                >
                                  <Unlock className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => setConfirmAction({ agent, kind: 'block', reason: '' })}
                                  className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-colors"
                                  title={t('حظر مؤقت', 'Temporary block')}
                                  data-testid={`agent-block-${agent.id}`}
                                >
                                  <Lock className="h-4 w-4" />
                                </button>
                              )}

                              {/* Archive / Delete — only enabled if blocked */}
                              <button
                                onClick={() => {
                                  if (!isBlocked) return;
                                  setConfirmAction({ agent, kind: 'archive', reason: '' });
                                }}
                                disabled={!isBlocked}
                                className={`p-2 rounded-lg border transition-colors ${
                                  isBlocked
                                    ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 cursor-pointer'
                                    : 'bg-white/[0.02] border-white/10 text-white/30 cursor-not-allowed'
                                }`}
                                title={isBlocked ? t('أرشفة / حذف', 'Archive / Delete') : t('يجب حظر الوكيل أولاً قبل الأرشفة', 'Block the agent first before archiving')}
                                data-testid={`agent-archive-${agent.id}`}
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {isBlocked && agent.agent_blocked_reason && (
                            <div className="mt-3 pt-3 border-t border-red-500/10 text-xs text-red-300/80" data-testid={`agent-blocked-reason-${agent.id}`}>
                              <span className="text-red-400 font-semibold">{t('سبب الحظر:', 'Block reason:')}</span> {agent.agent_blocked_reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {/* Agent Invites Section - دعوات الوكلاء (بالبريد + كود + صلاحيات + 7 أيام صلاحية) */}
            {activeTab === 'invitation-codes' && (
              <div data-testid="agent-invites-tab-embedded">
                <AgentInvites embedded />
              </div>
            )}
      {/* View Agent Details Modal */}
      {viewAgent && (() => {
        // Try to find the original request for this agent (if loaded)
        const req = (agentRequests || []).find(r => r.agent_id === viewAgent.agent_id || r.user_id === viewAgent.id);
        const details = extractAgentDetails(req || { user: viewAgent });
        return (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewAgent(null)}
            data-testid="agent-view-overlay"
          >
            <div
              className="bg-[#0a0a14] border border-cyan-500/40 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="agent-view-modal"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-cyan-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">{viewAgent.name}</h3>
                    <p className="text-xs text-cyan-400 font-mono">{viewAgent.agent_id}</p>
                  </div>
                </div>
                <button onClick={() => setViewAgent(null)} className="text-white/60 hover:text-white p-1" data-testid="agent-view-close">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Contact + membership */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="px-3 py-2 bg-white/5 rounded-lg flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#606060]">{t('البريد الإلكتروني', 'Email')}</p>
                    <p className="text-white text-sm truncate">{viewAgent.email}</p>
                  </div>
                </div>
                {viewAgent.phone && (
                  <div className="px-3 py-2 bg-white/5 rounded-lg flex items-center gap-2">
                    <Phone className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-[#606060]">{t('رقم الجوّال', 'Phone')}</p>
                      <p className="text-white text-sm font-mono" dir="ltr">{viewAgent.phone}</p>
                    </div>
                  </div>
                )}
                <div className="px-3 py-2 bg-white/5 rounded-lg flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#606060]">{t('تاريخ الانضمام', 'Joined On')}</p>
                    <p className="text-white text-sm">{viewAgent.agent_approved_at ? new Date(viewAgent.agent_approved_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '-'}</p>
                  </div>
                </div>
                <div className="px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-cyan-300/60">{t('رقم العضوية', 'Membership #')}</p>
                    <p className="text-cyan-300 text-sm font-mono font-bold">{viewAgent.agent_id}</p>
                  </div>
                </div>
              </div>

              {/* Application details (from original request if available) */}
              {(details.cv_url || details.languages.length > 0 || details.bio || details.skills.length > 0 || details.experience || details.nationality || details.city) ? (
                <>
                  <h4 className="text-xs font-bold text-[#A1A1AA] uppercase mb-2 mt-4">{t('تفاصيل التقديم', 'Application Details')}</h4>
                  <div className="space-y-2.5">
                    {details.cv_url && (
                      <button
                        type="button"
                        onClick={() => openCV(details.cv_url, { isAr })}
                        className="w-full flex items-center gap-2 px-3 py-2.5 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-lg hover:bg-blue-500/20 transition text-left"
                        data-testid="agent-view-cv-link"
                      >
                        <FileText className="h-4 w-4" />
                        <span className="text-sm">{t('عرض السيرة الذاتية (CV)', 'View CV')}</span>
                        <ExternalLink className="h-3.5 w-3.5 ms-auto" />
                      </button>
                    )}
                    {details.languages.length > 0 && (
                      <div className="px-3 py-2 bg-white/5 rounded-lg">
                        <p className="text-[10px] text-[#606060] mb-1 flex items-center gap-1.5"><Languages className="h-3 w-3" /> {t('اللغات', 'Languages')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {details.languages.map((lang, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-purple-500/20 text-purple-300">
                              {lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {details.experience && (
                        <div className="px-3 py-2 bg-white/5 rounded-lg">
                          <p className="text-[10px] text-[#606060] mb-1 flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> {t('الخبرة', 'Experience')}</p>
                          <p className="text-white text-sm">{details.experience}</p>
                        </div>
                      )}
                      {details.nationality && (
                        <div className="px-3 py-2 bg-white/5 rounded-lg">
                          <p className="text-[10px] text-[#606060] mb-1 flex items-center gap-1.5"><Award className="h-3 w-3" /> {t('الجنسية', 'Nationality')}</p>
                          <p className="text-white text-sm">{details.nationality}</p>
                        </div>
                      )}
                      {details.city && (
                        <div className="px-3 py-2 bg-white/5 rounded-lg">
                          <p className="text-[10px] text-[#606060] mb-1 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {t('المدينة', 'City')}</p>
                          <p className="text-white text-sm">{details.city}</p>
                        </div>
                      )}
                      {details.education && (
                        <div className="px-3 py-2 bg-white/5 rounded-lg">
                          <p className="text-[10px] text-[#606060] mb-1">{t('المؤهلات', 'Qualifications')}</p>
                          <p className="text-white text-sm">{details.education}</p>
                        </div>
                      )}
                    </div>
                    {details.skills.length > 0 && (
                      <div className="px-3 py-2 bg-white/5 rounded-lg">
                        <p className="text-[10px] text-[#606060] mb-1.5">{t('المهارات', 'Skills')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(Array.isArray(details.skills) ? details.skills : [details.skills]).map((sk, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-300">
                              {sk}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {details.bio && (
                      <div className="px-3 py-2 bg-white/5 rounded-lg">
                        <p className="text-[10px] text-[#606060] mb-1">{t('نبذة / رسالة الوكيل', 'Bio / Cover Letter')}</p>
                        <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{details.bio}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-4 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg text-center">
                  <p className="text-xs text-amber-300/80">{t('لا توجد تفاصيل تقديم محفوظة لهذا الوكيل.', 'No application details saved for this agent.')}</p>
                </div>
              )}

              <Button
                onClick={() => setViewAgent(null)}
                variant="outline"
                className="w-full mt-5 border-white/20 text-white hover:bg-white/5"
                data-testid="agent-view-close-btn"
              >
                {t('إغلاق', 'Close')}
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Confirmation Modal for Block / Unblock / Archive */}
      {confirmAction && (() => {
        const { agent, kind } = confirmAction;
        const cfg = {
          block: {
            title: t('حظر الوكيل مؤقتاً', 'Temporarily block agent'),
            icon: <Lock className="h-6 w-6 text-amber-400" />,
            body: t('سيتم حظر هذا الوكيل مؤقتاً ولن يستطيع استلام مهام جديدة. يمكنك فك الحظر لاحقاً.', 'This agent will be blocked and cannot receive new tasks. You can unblock them later.'),
            confirmLabel: t('حظر', 'Block'),
            confirmClass: 'bg-amber-500 hover:bg-amber-600 text-black',
            border: 'border-amber-500/40',
            askReason: true,
          },
          unblock: {
            title: t('فك الحظر عن الوكيل', 'Unblock agent'),
            icon: <Unlock className="h-6 w-6 text-emerald-400" />,
            body: t('سيعود الوكيل إلى حالته الطبيعية ويمكنه استلام المهام مجدداً.', 'The agent will resume normal status and can receive tasks again.'),
            confirmLabel: t('فك الحظر', 'Unblock'),
            confirmClass: 'bg-emerald-600 hover:bg-emerald-700 text-white',
            border: 'border-emerald-500/40',
            askReason: false,
          },
          archive: {
            title: t('أرشفة الوكيل', 'Archive agent'),
            icon: <Archive className="h-6 w-6 text-red-400" />,
            body: t('سيتم نقل الوكيل إلى الأرشيف. لن يظهر في القائمة النشطة، ويمكن استعادته من الأرشيف لاحقاً.', 'The agent will be moved to the archive. They will not appear in the active list, but can be restored later.'),
            confirmLabel: t('أرشفة', 'Archive'),
            confirmClass: 'bg-red-500 hover:bg-red-600 text-white',
            border: 'border-red-500/40',
            askReason: true,
          },
        }[kind];
        return (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setConfirmAction(null)}
            data-testid="agent-confirm-overlay"
          >
            <div
              className={`bg-[#0a0a14] border ${cfg.border} rounded-2xl p-6 max-w-md w-full`}
              onClick={(e) => e.stopPropagation()}
              data-testid="agent-confirm-modal"
            >
              <div className="flex items-center gap-3 mb-4">
                {cfg.icon}
                <h3 className="text-lg font-bold text-white">{cfg.title}</h3>
              </div>
              <p className="text-white/80 text-sm mb-2">{cfg.body}</p>
              <p className="text-white text-sm font-semibold mb-4">{agent.name}</p>

              {cfg.askReason && (
                <div className="mb-4">
                  <label className="block text-xs text-white/60 mb-1.5">
                    {t('السبب (اختياري)', 'Reason (optional)')}
                  </label>
                  <Input
                    value={confirmAction.reason || ''}
                    onChange={(e) => setConfirmAction((c) => ({ ...c, reason: e.target.value }))}
                    placeholder={t('مثال: مخالفة سياسة الاستخدام', 'Example: policy violation')}
                    className="bg-[#050505] border-white/10 text-white text-sm"
                    data-testid="agent-confirm-reason-input"
                  />
                </div>
              )}

              {kind === 'archive' && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300/90">
                    {t('الوكيل حالياً محظور. الأرشفة تنقله إلى قائمة الأرشيف. الحذف النهائي يتم من قائمة الأرشيف.',
                       'The agent is currently blocked. Archiving moves them to the archive list. Permanent deletion happens from the archive.')}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => setConfirmAction(null)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                  data-testid="agent-confirm-cancel"
                >
                  {t('إلغاء', 'Cancel')}
                </Button>
                <Button
                  onClick={async () => {
                    const reason = confirmAction.reason || '';
                    let ok = false;
                    if (kind === 'block') ok = await blockAgent(agent.id, reason);
                    if (kind === 'unblock') ok = await unblockAgent(agent.id);
                    if (kind === 'archive') ok = await archiveAgent(agent.id, reason);
                    if (ok) setConfirmAction(null);
                  }}
                  className={`flex-1 ${cfg.confirmClass}`}
                  data-testid="agent-confirm-btn"
                >
                  {cfg.confirmLabel}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      <AgentDiagnosticDialog open={diagOpen} onClose={() => setDiagOpen(false)} token={token} />
    </>
  );
}
